"use client";

import { AlertTriangle, ArrowRight, Gauge } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@repo/ui/components/ui/badge";
import { Progress } from "@repo/ui/components/ui/progress";
import { useAuth } from "../hooks/api/auth/useAuth";
import {
  useGroupUsageStats,
  useSubscriptionLimits,
} from "../hooks/api/subscription/use-group-subscription";
import { formatFreeParcoursTypeLabel } from "../hooks/use-free-access-check";
import { useGetCurrentUser } from "../hooks/api/users/use-current-user";

export function UsageLimitBanner() {
  const { isAdmin } = useAuth();
  const { data: user, isLoading: isUserLoading } = useGetCurrentUser();
  const groupeId = user?.data?.groupe?.id as number | undefined;
  const { data: stats, isLoading: isStatsLoading } = useGroupUsageStats(groupeId);
  const limits = useSubscriptionLimits(groupeId);

  if (isUserLoading || isStatsLoading || !groupeId || isAdmin) return null;
  if (limits.canCreateArbre) return null;

  const isFreeLimit = limits.isFreeAccount && limits.isParcoursUsed;
  const title = isFreeLimit ? "Parcours gratuit utilisé" : "Quota mensuel atteint";
  const message = isFreeLimit
    ? `Votre parcours gratuit (${formatFreeParcoursTypeLabel(limits.parcoursType)}) a été utilisé. Passez à un abonnement pour créer de nouveaux inventaires.`
    : `Vous avez utilisé ${stats?.monthlyParcours ?? 0} / ${stats?.maxParcours ?? 0} parcours ce mois. Augmentez votre forfait pour continuer.`;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex flex-col gap-2 border-b border-orange-300/60 bg-gradient-to-r from-orange-50 to-red-50/80 px-4 py-3 dark:border-orange-800/40 dark:from-orange-950/50 dark:to-red-950/30 sm:flex-row sm:items-center sm:justify-between sm:gap-3 print:hidden"
    >
      <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 ring-1 ring-orange-300/60 dark:bg-orange-900/60 dark:ring-orange-700/40">
          <Gauge className="h-4 w-4 text-orange-600 dark:text-orange-400" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <AlertTriangle
              className="h-3.5 w-3.5 shrink-0 text-orange-600 dark:text-orange-400"
              aria-hidden="true"
            />
            <span className="text-sm font-semibold text-orange-900 dark:text-orange-200">
              {title}
            </span>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              {limits.planType}
            </Badge>
          </div>
          <p className="text-xs leading-relaxed text-orange-800/90 dark:text-orange-300/90">
            {message}
          </p>
          {!isFreeLimit && stats && stats.maxParcours > 0 && (
            <Progress value={100} className="h-1.5 max-w-xs [&>div]:bg-orange-500" />
          )}
        </div>
      </div>

      <Link
        to="/admin/subscription"
        className="flex shrink-0 items-center justify-center gap-1.5 self-start rounded-md border border-orange-400/60 bg-orange-100 px-3 py-1.5 text-xs font-semibold text-orange-900 transition-colors duration-150 hover:border-orange-500 hover:bg-orange-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 dark:border-orange-700/60 dark:bg-orange-900/50 dark:text-orange-200 dark:hover:bg-orange-900/80 sm:self-center"
        aria-label="Augmenter mon forfait et gérer mon abonnement"
      >
        Augmenter mon forfait
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </div>
  );
}
